import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useStepNavigation } from '../hooks/useStepNavigation'
import { FormData, StepId } from '../types'
import { STEPS } from '../config/steps.config'
import { SingleChoiceQuestion } from './SingleChoiceQuestion'
import { ProgressIndicator } from './ProgressIndicator'
import { enrichContact } from '../utils/contact-enrichment'

interface FormContainerProps {
  onComplete: (formData: Partial<FormData>) => void
  workspaceId: string
}

// Map each step to its display order (excluding completed step)
const STEP_ORDER: Record<StepId, number> = {
  role: 1,
  useCase: 2,
  companySize: 3,
  discoverySource: 4,
  youtubeChannel: 4, // Same level as discoverySource (conditional)
  completed: 0, // Not shown in progress
}

export function FormContainer({ onComplete, workspaceId }: FormContainerProps) {
  const { currentStepId, goToNextStep } = useStepNavigation()
  const [formData, setFormData] = useState<Partial<FormData>>({})

  // Calculate current step number based on the step's order
  const totalSteps = 4
  const currentStepNumber = STEP_ORDER[currentStepId] || 0

  const handleAnswer = (fieldName: keyof FormData, value: string) => {
    const newFormData = { ...formData, [fieldName]: value }
    setFormData(newFormData)

    if (currentStepId !== 'completed') {
      goToNextStep(newFormData)
    }
  }

  const currentStep = STEPS.find((step) => step.id === currentStepId)

  if (currentStepId === 'completed') {
    enrichContact({
      formData,
      workspaceId,
    }).catch((error) => {
      console.error('Error enriching contact:', error)
    })

    onComplete(formData)
    return null
  }

  if (!currentStep) {
    return <div>Error: Step not found</div>
  }

  return (
    <div className="relative h-full min-h-screen w-full overflow-auto">
      <div className="absolute left-0 top-4 w-full px-4 md:left-12 md:top-12 md:px-0">
        <ProgressIndicator currentStep={currentStepNumber} totalSteps={totalSteps} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStepId}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="h-full"
        >
          <SingleChoiceQuestion
            question={currentStep.question}
            description={currentStep.description}
            options={currentStep.options}
            randomizeOptions={currentStep.randomizeOptions}
            onSelect={(value) => handleAnswer(currentStep.fieldName, value)}
          />
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
